'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from '@/shared/navigation'
import { executeWasteBatch } from '@/features/kitchen/api/executeWaste'
import { uploadPhoto } from '@/shared/api/uploadPhoto'
import { fetchTodayWasteBatches } from '@/features/kitchen/api/getTodayWaste'
import { fetchKitchenStock, fetchKitchenWasteStock } from '@/features/kitchen/api/getDispatchOptions'
import { shouldUseKitchenApi } from '@/features/kitchen/api/kitchenApiMode'
import { mapWasteBatchesToUi } from '@/features/kitchen/api/mapWasteFromApi'
import { submitWasteProposal } from '@/features/kitchen/api/submitWasteProposal'
import { kitchenTerms } from '@/features/kitchen/kitchenTerms'
import { loadWasteBatches, saveWasteBatches } from '@/features/kitchen/storage/wasteStorage'
import { FlowStepper } from '@/shared/components/FlowStepper'
import { PinKeypadModal } from '@/shared/components/PinKeypadModal'
import { QtyStepper } from '@/shared/components/QtyStepper'
import { SkeletonList } from '@/shared/components/Skeleton'
import { StickyActionFooter } from '@/shared/components/StickyActionFooter'
import { Toast } from '@/shared/components/Toast'
import { getKitchenDevice } from '@/shared/auth/deviceStorage'
import { consumeLastSyncQueued } from '@/shared/sync/kitchenSync'

type WasteStatus = 'draft' | 'submitted' | 'approved' | 'rejected' | 'done'

type StockSource = 'main' | 'return' | 'damaged'

type WasteItem = {
  id: string
  productName: string
  productionDate: string
  stockSource: StockSource
  qty: number
  reason?: string
}

type WasteBatch = {
  id: string
  batchId?: string
  status: WasteStatus
  items: WasteItem[]
  proposalNote: string
  executionNote: string
  photos: string[]
  updatedAt: string
  submittedAt?: string
  approvedAt?: string
  executedAt?: string
  rejectionReason?: string
}

type StockItem = {
  productName: string
  productionDate: string
  source: StockSource
  stockAvailable: number
}

const STOCK_SOURCES: {
  key: StockSource
  label: string
  shortLabel: string
  hint: string
  icon: string
  chipClass: string
}[] = [
  {
    key: 'main',
    label: 'Good stock',
    shortLabel: 'Good stock',
    hint: 'Stok bagus di dapur yang siap kirim — wajib isi alasan per produk.',
    icon: 'inventory_2',
    chipClass: 'bg-surface-container text-on-surface-variant border border-outline-variant',
  },
  {
    key: 'return',
    label: 'Waste stock',
    shortLabel: 'Waste stock',
    hint: 'Stok waste dari retur toko — tidak perlu alasan (memang harus dimusnahkan).',
    icon: 'delete_sweep',
    chipClass: 'bg-secondary-fixed text-on-secondary-fixed-variant border border-secondary-fixed-dim',
  },
]

const BASE_STOCK: StockItem[] = [
  { productName: 'Roti coklat', productionDate: '23/06/26', source: 'main', stockAvailable: 70 },
  { productName: 'Roti keju', productionDate: '23/06/26', source: 'main', stockAvailable: 40 },
  { productName: 'Donat gula', productionDate: '23/06/26', source: 'main', stockAvailable: 10 },
  { productName: 'Croissant', productionDate: '23/06/26', source: 'main', stockAvailable: 12 },
  { productName: 'Donat gula', productionDate: '23/06/26', source: 'return', stockAvailable: 15 },
  { productName: 'Roti coklat', productionDate: '22/06/26', source: 'return', stockAvailable: 8 },
  { productName: 'Croissant', productionDate: '22/06/26', source: 'return', stockAvailable: 6 },
  { productName: 'Roti coklat', productionDate: '22/06/26', source: 'damaged', stockAvailable: 5 },
  { productName: 'Roti keju', productionDate: '20/06/26', source: 'damaged', stockAvailable: 6 },
]

type View = 'list' | 'proposal' | 'execute' | 'detail'
type ListFilter = 'draft' | 'submitted' | 'approved' | 'done'
type EditorStep = 'edit' | 'review'
type PinMode = 'submit' | 'execute'
type ConfirmAction =
  | { type: 'deleteItem'; itemId: string; label: string }
  | { type: 'deleteBatch'; batchId: string }

const INITIAL_BATCHES: WasteBatch[] = [
  {
    id: 'DSP-KIT-20260623-01',
    status: 'draft',
    updatedAt: 'Baru dibuat',
    proposalNote: 'Sisa closing kemarin',
    executionNote: '',
    photos: [],
    items: [
      {
        id: 'w1',
        productName: 'Roti coklat',
        productionDate: '22/06/26',
        stockSource: 'damaged',
        qty: 5,
      },
      {
        id: 'w2',
        productName: 'Donat gula',
        productionDate: '23/06/26',
        stockSource: 'return',
        qty: 2,
      },
    ],
  },
  {
    id: 'DSP-KIT-20260622-02',
    status: 'submitted',
    updatedAt: 'Dikirim 22/06 15:20',
    submittedAt: '22/06 15:20',
    proposalNote: 'Batch rusak oven',
    executionNote: '',
    photos: [],
    items: [
      {
        id: 'w3',
        productName: 'Croissant',
        productionDate: '22/06/26',
        stockSource: 'damaged',
        qty: 8,
      },
    ],
  },
  {
    id: 'DSP-KIT-20260621-03',
    status: 'approved',
    updatedAt: 'Disetujui HO 21/06 10:00',
    submittedAt: '20/06 16:30',
    approvedAt: '21/06 10:00',
    proposalNote: 'Expired storage',
    executionNote: '',
    photos: [],
    items: [
      {
        id: 'w4',
        productName: 'Roti keju',
        productionDate: '20/06/26',
        stockSource: 'damaged',
        qty: 6,
      },
      {
        id: 'w5',
        productName: 'Roti coklat',
        productionDate: '20/06/26',
        stockSource: 'main',
        qty: 4,
        reason: 'Kadaluarsa hari ini',
      },
    ],
  },
  {
    id: 'DSP-KIT-20260620-05',
    status: 'rejected',
    updatedAt: 'Ditolak HO 20/06 09:15',
    submittedAt: '19/06 17:00',
    rejectionReason: 'Qty donat melebihi stok retur tercatat. Perbaiki jumlah lalu kirim ulang.',
    proposalNote: 'Retur toko kemarin',
    executionNote: '',
    photos: [],
    items: [
      {
        id: 'w7',
        productName: 'Donat gula',
        productionDate: '23/06/26',
        stockSource: 'return',
        qty: 20,
      },
    ],
  },
  {
    id: 'DSP-KIT-20260618-04',
    status: 'done',
    updatedAt: 'Selesai 18/06 11:45',
    submittedAt: '17/06 14:00',
    approvedAt: '18/06 08:00',
    executedAt: '18/06 11:45',
    proposalNote: 'Sisa closing',
    executionNote: 'Sudah dibuang ke tempat sampah dapur',
    photos: ['mock-photo'],
    items: [
      {
        id: 'w6',
        productName: 'Donat gula',
        productionDate: '17/06/26',
        stockSource: 'main',
        qty: 10,
        reason: 'Sisa closing tidak laku',
      },
    ],
  },
]

function stockBucketKey(source: StockSource, productName: string, productionDate: string): string {
  return `${source}|${productName}|${productionDate}`
}

function isGoodStockSource(source: StockSource): boolean {
  return source === 'main'
}

function normalizeStockSource(source: StockSource): 'main' | 'return' {
  return source === 'main' ? 'main' : 'return'
}

function stockSourceLabel(source: StockSource): string {
  return stockSourceMeta(source).label
}

function statusLabel(status: WasteStatus): string {
  if (status === 'draft') return 'Belum dikirim'
  if (status === 'submitted') return 'Menunggu HO'
  if (status === 'approved') return 'Siap dimusnahkan'
  if (status === 'rejected') return 'Ditolak HO'
  return 'Selesai'
}

function statusBadge(status: WasteStatus): string {
  if (status === 'draft') return 'badge-draft'
  if (status === 'submitted') return 'badge-warning'
  if (status === 'approved') return 'badge-success'
  if (status === 'rejected') return 'badge-danger'
  return 'badge-success'
}

function formatNowLabel(prefix: string): string {
  const now = new Date()
  const day = new Intl.DateTimeFormat('id-ID', { day: '2-digit', month: '2-digit' }).format(now)
  const time = new Intl.DateTimeFormat('id-ID', { hour: '2-digit', minute: '2-digit' }).format(now)
  return `${prefix} ${day} ${time}`
}

function getFilledItems(items: WasteItem[]): WasteItem[] {
  return items.filter((item) => item.qty > 0 && item.productName.trim())
}

function nextBatchId(batches: WasteBatch[]): string {
  const dateRef = new Date().toISOString().slice(0, 10).replace(/-/g, '')
  const count = batches.filter((b) => b.id.includes(dateRef)).length + 1
  return `DSP-KIT-${dateRef}-${String(count).padStart(2, '0')}`
}

function reservesStock(status: WasteStatus): boolean {
  return status !== 'rejected'
}

function computeReservedStock(
  batches: WasteBatch[],
  excludeBatchId?: string | null,
  baseStock: StockItem[] = BASE_STOCK,
): Map<string, number> {
  const remaining = new Map(
    baseStock.map((item) => [
      stockBucketKey(item.source, item.productName, item.productionDate),
      item.stockAvailable,
    ]),
  )

  for (const batch of batches) {
    if (batch.id === excludeBatchId || !reservesStock(batch.status)) continue
    if (batch.status === 'done') continue
    for (const item of getFilledItems(batch.items)) {
      const key = stockBucketKey(item.stockSource, item.productName, item.productionDate)
      remaining.set(key, (remaining.get(key) ?? 0) - item.qty)
    }
  }

  for (const batch of batches) {
    if (batch.status !== 'done') continue
    for (const item of getFilledItems(batch.items)) {
      const key = stockBucketKey(item.stockSource, item.productName, item.productionDate)
      remaining.set(key, (remaining.get(key) ?? 0) - item.qty)
    }
  }

  return remaining
}

function getItemStockLimit(
  batches: WasteBatch[],
  batchId: string,
  source: StockSource,
  productName: string,
  productionDate: string,
  baseStock: StockItem[] = BASE_STOCK,
): number {
  const remaining = computeReservedStock(batches, batchId, baseStock)
  const key = stockBucketKey(source, productName, productionDate)
  return Math.max(0, remaining.get(key) ?? 0)
}

function stockSourceMeta(source: StockSource) {
  const key = normalizeStockSource(source)
  return STOCK_SOURCES.find((s) => s.key === key) ?? STOCK_SOURCES[0]
}

function summarizeQtyBySource(items: WasteItem[]): Record<StockSource, number> {
  const totals: Record<StockSource, number> = { main: 0, return: 0, damaged: 0 }
  for (const item of getFilledItems(items)) {
    totals[item.stockSource] += item.qty
  }
  return totals
}

function formatSourceBreakdown(items: WasteItem[]): string {
  const totals = summarizeQtyBySource(items)
  return STOCK_SOURCES.filter((s) => totals[s.key] > 0)
    .map((s) => `${s.shortLabel} ${totals[s.key]}`)
    .join(' · ')
}

function computeAvailableBySource(
  remainingStock: Map<string, number>,
  baseStock: StockItem[] = BASE_STOCK,
): Record<StockSource, number> {
  const totals: Record<StockSource, number> = { main: 0, return: 0, damaged: 0 }
  for (const item of baseStock) {
    const key = stockBucketKey(item.source, item.productName, item.productionDate)
    totals[item.source] += Math.max(0, remainingStock.get(key) ?? 0)
  }
  return totals
}

function wasteFlowActiveStep(status: WasteStatus): number {
  if (status === 'done') return 5
  if (status === 'approved') return 3
  if (status === 'submitted' || status === 'rejected') return 2
  return 1
}

const WASTE_FLOW_STEPS = [
  { label: 'Buat' },
  { label: 'Kirim HO' },
  { label: 'Setuju' },
  { label: 'Musnah' },
] as const

function StockSourceChip({ source, compact = false }: { source: StockSource; compact?: boolean }) {
  const meta = stockSourceMeta(source)
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-label-md font-semibold ${meta.chipClass} ${
        compact ? 'text-[10px] px-2 py-0.5' : 'text-[11px] px-2.5 py-1'
      }`}
    >
      <span className="material-symbols-outlined text-sm leading-none">{meta.icon}</span>
      {compact ? meta.shortLabel : meta.label}
    </span>
  )
}

function ItemReasonBlock({ reason }: { reason?: string }) {
  if (!reason?.trim()) return null
  return (
    <div className="rounded-lg bg-surface-container px-3 py-2">
      <p className="text-label-md text-on-surface-variant">Alasan</p>
      <p className="text-body-sm text-on-surface mt-0.5">{reason}</p>
    </div>
  )
}

function WasteTimeline({ batch }: { batch: WasteBatch }) {
  const events = [
    batch.submittedAt ? { label: 'Dikirim ke HO', at: batch.submittedAt, icon: 'send' } : null,
    batch.status === 'rejected' && batch.rejectionReason
      ? { label: 'Ditolak HO', at: batch.updatedAt, icon: 'block', detail: batch.rejectionReason }
      : batch.approvedAt
        ? { label: 'Disetujui HO', at: batch.approvedAt, icon: 'check_circle' }
        : null,
    batch.executedAt
      ? { label: 'Pemusnahan selesai', at: batch.executedAt, icon: 'delete_sweep' }
      : null,
  ].filter(Boolean) as { label: string; at: string; icon: string; detail?: string }[]

  if (events.length === 0) return null

  return (
    <div className="rounded-lg border border-outline-variant p-3 space-y-0">
      <p className="text-label-md font-semibold text-on-surface-variant mb-3">Riwayat</p>
      {events.map((event, index) => (
        <div key={`${event.label}-${index}`} className="flex gap-3">
          <div className="flex flex-col items-center">
            <div
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                event.label === 'Ditolak HO'
                  ? 'bg-error-container text-error'
                  : 'bg-primary-container text-on-primary-container'
              }`}
            >
              <span className="material-symbols-outlined text-base">{event.icon}</span>
            </div>
            {index < events.length - 1 && <div className="w-0.5 flex-1 bg-outline-variant my-1 min-h-4" />}
          </div>
          <div className={`pb-4 ${index === events.length - 1 ? 'pb-0' : ''}`}>
            <p className="text-body-sm font-semibold text-on-surface">{event.label}</p>
            <p className="text-label-md text-on-surface-variant mt-0.5">{event.at}</p>
            {event.detail && (
              <p className="text-body-sm text-error mt-1 bg-error-container/60 rounded-lg px-2 py-1.5">
                {event.detail}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

function ConfirmDialog({
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  title: string
  message: string
  confirmLabel: string
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <div className="fixed inset-0 z-40 bg-inverse-surface/40 flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-surface-card border border-outline-variant rounded-xl shadow-sm p-4 space-y-4">
        <div>
          <p className="text-headline-md text-primary">{title}</p>
          <p className="text-body-sm text-on-surface-variant mt-2">{message}</p>
        </div>
        <div className="flex gap-2">
          <button type="button" className="btn-outline flex-1 min-h-[44px]" onClick={onCancel}>
            Batal
          </button>
          <button
            type="button"
            className="btn-danger flex-1"
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

export function KitchenWastePage() {
  const useApi = shouldUseKitchenApi()
  const device = getKitchenDevice()
  const [batches, setBatches] = useState<WasteBatch[]>(() =>
    useApi ? [] : loadWasteBatches(INITIAL_BATCHES),
  )
  const [isLoading, setIsLoading] = useState(useApi)
  const [view, setView] = useState<View>('list')
  const [listFilter, setListFilter] = useState<ListFilter>('draft')
  const [editorStep, setEditorStep] = useState<EditorStep>('edit')
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null)
  const [isAddItemOpen, setIsAddItemOpen] = useState(false)
  const [newItemSource, setNewItemSource] = useState<StockSource>('main')
  const [newItemProduct, setNewItemProduct] = useState('')
  const [newItemQty, setNewItemQty] = useState(1)
  const [addItemError, setAddItemError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [pinMode, setPinMode] = useState<PinMode | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null)
  const [stockItems, setStockItems] = useState<StockItem[]>(useApi ? [] : BASE_STOCK)

  useEffect(() => {
    if (useApi) return
    saveWasteBatches(batches)
  }, [batches, useApi])

  useEffect(() => {
    if (!useApi || !device?.deviceId) {
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    fetchTodayWasteBatches(device.deviceId)
      .then((data) => {
        if (cancelled) return
        setBatches(mapWasteBatchesToUi(data.batches))
      })
      .catch(() => {
        if (!cancelled) setBatches([])
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [useApi, device?.deviceId])

  useEffect(() => {
    if (!useApi || !device?.deviceId) return

    let cancelled = false
    Promise.all([
      fetchKitchenStock(device.deviceId).catch(() => []),
      fetchKitchenWasteStock(device.deviceId).catch(() => []),
    ])
      .then(([good, waste]) => {
        if (cancelled) return
        const goodItems = good.map((s) => ({
          productName: s.productName,
          productionDate: s.productionDate,
          source: 'main' as StockSource,
          stockAvailable: s.stockAvailable,
        }))
        const wasteItems = waste.map((s) => ({
          productName: s.productName,
          productionDate: s.productionDate,
          source: 'return' as StockSource,
          stockAvailable: s.stockAvailable,
        }))
        setStockItems([...goodItems, ...wasteItems])
      })
      .catch(() => {
        if (!cancelled) setStockItems([])
      })

    return () => {
      cancelled = true
    }
  }, [useApi, device?.deviceId, batches])

  const kitchenName = device?.kitchenName ?? 'Dapur'

  const today = useMemo(
    () =>
      new Intl.DateTimeFormat('id-ID', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }).format(new Date()),
    [],
  )

  const dismissToast = useCallback(() => setToast(null), [])
  const showToast = useCallback((message: string) => setToast(message), [])

  const selectedBatch = batches.find((b) => b.id === selectedBatchId) ?? null

  const draftBatches = useMemo(() => batches.filter((b) => b.status === 'draft'), [batches])
  const submittedBatches = useMemo(
    () => batches.filter((b) => b.status === 'submitted' || b.status === 'rejected'),
    [batches],
  )
  const approvedBatches = useMemo(() => batches.filter((b) => b.status === 'approved'), [batches])
  const doneBatches = useMemo(() => batches.filter((b) => b.status === 'done'), [batches])

  const visibleBatches = useMemo(() => {
    if (listFilter === 'draft') return draftBatches
    if (listFilter === 'submitted') return submittedBatches
    if (listFilter === 'approved') return approvedBatches
    return doneBatches
  }, [listFilter, draftBatches, submittedBatches, approvedBatches, doneBatches])

  const remainingStock = useMemo(
    () => computeReservedStock(batches, selectedBatchId, stockItems),
    [batches, selectedBatchId, stockItems],
  )

  function openList(filter?: ListFilter) {
    setView('list')
    setSelectedBatchId(null)
    setEditorStep('edit')
    setSaveError(null)
    if (filter) setListFilter(filter)
  }

  function createBatch() {
    const id = nextBatchId(batches)
    const batch: WasteBatch = {
      id,
      status: 'draft',
      items: [],
      proposalNote: '',
      executionNote: '',
      photos: [],
      updatedAt: 'Baru dibuat',
    }
    setBatches((prev) => [batch, ...prev])
    setSelectedBatchId(id)
    setEditorStep('edit')
    setView('proposal')
    setListFilter('draft')
    showToast(`${kitchenTerms.waste.proposal} baru dibuat.`)
  }

  function openProposal(id: string) {
    setSelectedBatchId(id)
    setEditorStep('edit')
    setView('proposal')
    setSaveError(null)
  }

  function openExecute(id: string) {
    setSelectedBatchId(id)
    setView('execute')
    setSaveError(null)
  }

  function openDetail(id: string) {
    setSelectedBatchId(id)
    setView('detail')
    setSaveError(null)
  }

  function updateBatch(patch: Partial<WasteBatch>) {
    if (!selectedBatchId) return
    setBatches((prev) => prev.map((b) => (b.id === selectedBatchId ? { ...b, ...patch } : b)))
  }

  function updateItem(itemId: string, patch: Partial<WasteItem>) {
    if (!selectedBatchId) return
    setBatches((prev) =>
      prev.map((b) => {
        if (b.id !== selectedBatchId) return b
        return {
          ...b,
          items: b.items.map((item) => (item.id === itemId ? { ...item, ...patch } : item)),
        }
      }),
    )
  }

  function requestRemoveItem(itemId: string, label: string) {
    setConfirmAction({ type: 'deleteItem', itemId, label })
  }

  function requestDeleteBatch(batchId: string) {
    setConfirmAction({ type: 'deleteBatch', batchId })
  }

  function handleConfirmAction() {
    if (!confirmAction) return
    if (confirmAction.type === 'deleteItem') {
      removeItem(confirmAction.itemId)
      showToast('Item dihapus dari proposal.')
    } else {
      deleteBatch(confirmAction.batchId)
    }
    setConfirmAction(null)
  }

  function recreateFromRejected(batchId: string) {
    const source = batches.find((b) => b.id === batchId)
    if (!source || source.status !== 'rejected') return
    const id = nextBatchId(batches)
    const batch: WasteBatch = {
      id,
      status: 'draft',
      items: source.items.map((item) => ({ ...item, id: `w-${Date.now()}-${item.id}` })),
      proposalNote: source.proposalNote,
      executionNote: '',
      photos: [],
      updatedAt: 'Baru dibuat dari penolakan HO',
    }
    setBatches((prev) => [batch, ...prev])
    setSelectedBatchId(id)
    setEditorStep('edit')
    setView('proposal')
    setListFilter('draft')
    showToast('Proposal baru dibuat dari penolakan HO.')
  }

  function removeItem(itemId: string) {
    if (!selectedBatchId) return
    setBatches((prev) =>
      prev.map((b) =>
        b.id === selectedBatchId ? { ...b, items: b.items.filter((item) => item.id !== itemId) } : b,
      ),
    )
  }

  function selectSourceInModal(source: StockSource) {
    setNewItemSource(source)
    const first = stockItems.find((item) => {
      if (item.source !== source) return false
      const key = stockBucketKey(item.source, item.productName, item.productionDate)
      if ((remainingStock.get(key) ?? 0) <= 0) return false
      const duplicate = selectedBatch?.items.some(
        (line) =>
          line.stockSource === item.source &&
          line.productName === item.productName &&
          line.productionDate === item.productionDate,
      )
      return !duplicate
    })
    setNewItemProduct(
      first ? stockBucketKey(first.source, first.productName, first.productionDate) : '',
    )
    setNewItemQty(1)
    setAddItemError(null)
  }

  function openAddItem() {
    const firstSource =
      STOCK_SOURCES.find((source) =>
        stockItems.some((item) => {
          if (item.source !== source.key) return false
          const key = stockBucketKey(item.source, item.productName, item.productionDate)
          return (remainingStock.get(key) ?? 0) > 0
        }),
      )?.key ?? 'main'

    selectSourceInModal(firstSource)
    setIsAddItemOpen(true)
  }

  function confirmAddItem() {
    if (!selectedBatchId || !newItemProduct) {
      setAddItemError('Pilih produk dari stok dapur.')
      return
    }
    const parts = newItemProduct.split('|')
    const stockSource = parts[0] as StockSource
    const productName = parts[1]
    const productionDate = parts[2]
    if (!productName || !productionDate) {
      setAddItemError('Pilih produk dari stok dapur.')
      return
    }

    const limit = getItemStockLimit(
      batches,
      selectedBatchId,
      stockSource,
      productName,
      productionDate,
      stockItems,
    )
    if (newItemQty <= 0) {
      setAddItemError('Jumlah harus lebih dari 0.')
      return
    }
    if (newItemQty > limit) {
      setAddItemError(`Stok tidak cukup. Tersedia ${limit} pcs.`)
      return
    }

    const duplicate = selectedBatch?.items.some(
      (item) =>
        item.productName === productName &&
        item.productionDate === productionDate &&
        item.stockSource === stockSource,
    )
    if (duplicate) {
      setAddItemError('Produk ini sudah ada di proposal.')
      return
    }

    const item: WasteItem = {
      id: `w-${Date.now()}`,
      productName,
      productionDate,
      stockSource,
      qty: newItemQty,
    }
    setBatches((prev) =>
      prev.map((b) => (b.id === selectedBatchId ? { ...b, items: [...b.items, item] } : b)),
    )
    setIsAddItemOpen(false)
    setAddItemError(null)
  }

  function canDeleteBatch(batch: WasteBatch): boolean {
    return batch.status === 'draft' && getFilledItems(batch.items).length === 0
  }

  function deleteBatch(id: string) {
    const batch = batches.find((b) => b.id === id)
    if (!batch || !canDeleteBatch(batch)) return
    setBatches((prev) => prev.filter((b) => b.id !== id))
    if (selectedBatchId === id) openList('draft')
    showToast('Proposal kosong dihapus.')
  }

  function validateProposal(batchId: string, items: WasteItem[]): boolean {
    const filled = getFilledItems(items)
    if (filled.length === 0) {
      setSaveError('Tambahkan minimal 1 item dengan jumlah lebih dari 0.')
      return false
    }
    const over = filled.find((item) => {
      const limit = getItemStockLimit(
        batches,
        batchId,
        item.stockSource,
        item.productName,
        item.productionDate,
        stockItems,
      )
      return item.qty > limit
    })
    if (over) {
      const limit = getItemStockLimit(
        batches,
        batchId,
        over.stockSource,
        over.productName,
        over.productionDate,
        stockItems,
      )
      setSaveError(
        `Stok ${over.productName} (${stockSourceLabel(over.stockSource)}) tidak cukup. Tersedia ${limit} pcs.`,
      )
      return false
    }
    const missingReason = filled.find(
      (item) => isGoodStockSource(item.stockSource) && !item.reason?.trim(),
    )
    if (missingReason) {
      setSaveError(`Isi alasan untuk ${missingReason.productName} (good stock wajib ada alasan).`)
      return false
    }
    setSaveError(null)
    return true
  }

  function saveDraft() {
    if (!selectedBatch || selectedBatch.status !== 'draft') return
    if (!validateProposal(selectedBatch.id, selectedBatch.items)) return
    updateBatch({ updatedAt: formatNowLabel('Disimpan') })
    openList('draft')
    showToast(`${kitchenTerms.waste.proposal} disimpan.`)
  }

  function openSubmitReview() {
    if (!selectedBatch || selectedBatch.status !== 'draft') return
    if (!validateProposal(selectedBatch.id, selectedBatch.items)) return
    updateBatch({ updatedAt: formatNowLabel('Disimpan') })
    setEditorStep('review')
    setSaveError(null)
  }

  async function confirmSubmitToHo(pin: string) {
    if (!selectedBatch || !device) {
      throw new Error('Perangkat tidak terdaftar.')
    }
    const filled = getFilledItems(selectedBatch.items)
    const result = await submitWasteProposal({
      batchId: selectedBatch.batchId ?? selectedBatch.id,
      deviceId: device.deviceId,
      pin,
      lines: filled.map((item) => ({
        productName: item.productName,
        productionDate: item.productionDate,
        stockSource: normalizeStockSource(item.stockSource),
        qty: item.qty,
        reason: item.reason?.trim() || undefined,
      })),
      note: selectedBatch.proposalNote.trim() || undefined,
    })

    const label = formatNowLabel('Dikirim')
    setBatches((prev) =>
      prev.map((b) =>
        b.id === selectedBatch.id
          ? {
              ...b,
              status: 'submitted' as const,
              updatedAt: label,
              submittedAt: new Intl.DateTimeFormat('id-ID', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              }).format(new Date(result.submitted_at)),
            }
          : b,
      ),
    )
    setPinMode(null)
    openList('submitted')
    const queuedSuffix = consumeLastSyncQueued() ? ` ${kitchenTerms.sync.queued}` : ''
    showToast(`Proposal dikirim ke HO. Stok belum berubah.${queuedSuffix}`)
  }

  function handlePhotosSelected(files: FileList | null) {
    if (!files || !selectedBatchId) return
    const urls = Array.from(files).map((file) => URL.createObjectURL(file))
    updateBatch({ photos: [...(selectedBatch?.photos ?? []), ...urls] })
  }

  function removePhoto(index: number) {
    if (!selectedBatch) return
    const next = selectedBatch.photos.filter((_, i) => i !== index)
    updateBatch({ photos: next })
  }

  function openExecutePin() {
    if (!selectedBatch) return
    if (!selectedBatch.executionNote.trim()) {
      setSaveError('Catatan pemusnahan wajib diisi.')
      return
    }
    if (selectedBatch.photos.length < 1) {
      setSaveError('Minimal 1 foto bukti pemusnahan.')
      return
    }
    setSaveError(null)
    setPinMode('execute')
  }

  async function confirmExecute(pin: string) {
    if (!selectedBatch || !device) {
      throw new Error('Perangkat tidak terdaftar.')
    }
    const batchRef = selectedBatch.batchId ?? selectedBatch.id
    const uploaded = await Promise.all(
      selectedBatch.photos.map((photo) =>
        uploadPhoto(photo, 'disposal', { batchId: batchRef }),
      ),
    )
    const result = await executeWasteBatch({
      batchId: selectedBatch.batchId ?? selectedBatch.id,
      deviceId: device.deviceId,
      pin,
      executionNote: selectedBatch.executionNote.trim(),
      photoCount: uploaded.length,
      photoUrls: uploaded.map((u) => u.key),
    })

    const totalQty = getFilledItems(selectedBatch.items).reduce((sum, item) => sum + item.qty, 0)
    setBatches((prev) =>
      prev.map((b) =>
        b.id === selectedBatch.id
          ? {
              ...b,
              status: 'done' as const,
              updatedAt: formatNowLabel('Selesai'),
              executedAt: new Intl.DateTimeFormat('id-ID', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              }).format(new Date(result.executed_at)),
            }
          : b,
      ),
    )
    setPinMode(null)
    openList('done')
    const queuedSuffix = consumeLastSyncQueued() ? ` ${kitchenTerms.sync.queued}` : ''
    showToast(`${totalQty} pcs dimusnahkan. Stok dapur berkurang.${queuedSuffix}`)
  }

  const filledItems = selectedBatch ? getFilledItems(selectedBatch.items) : []
  const filledTotalQty = filledItems.reduce((sum, item) => sum + item.qty, 0)
  const isProposalEditable = selectedBatch?.status === 'draft'

  const stockOptionsForSource = useMemo(() => {
    return stockItems.filter((item) => {
      if (item.source !== newItemSource) return false
      const key = stockBucketKey(item.source, item.productName, item.productionDate)
      if ((remainingStock.get(key) ?? 0) <= 0) return false
      const duplicate = selectedBatch?.items.some(
        (line) =>
          line.stockSource === item.source &&
          line.productName === item.productName &&
          line.productionDate === item.productionDate,
      )
      return !duplicate
    })
  }, [newItemSource, remainingStock, selectedBatch, stockItems])

  const listEmptyMessage: Record<ListFilter, string> = {
    draft: `Belum ada ${kitchenTerms.waste.proposal.toLowerCase()}.`,
    submitted: 'Tidak ada proposal menunggu persetujuan HO.',
    approved: 'Tidak ada batch siap dimusnahkan.',
    done: 'Belum ada pemusnahan yang selesai.',
  }

  const availableBySource = useMemo(
    () => computeAvailableBySource(remainingStock, stockItems),
    [remainingStock, stockItems],
  )

  const newItemLimit = useMemo(() => {
    if (!newItemProduct || !selectedBatchId) return 0
    const parts = newItemProduct.split('|')
    const stockSource = parts[0] as StockSource
    const productName = parts[1]
    const productionDate = parts[2]
    if (!productName || !productionDate) return 0
    return getItemStockLimit(
      batches,
      selectedBatchId,
      stockSource,
      productName,
      productionDate,
      stockItems,
    )
  }, [newItemProduct, selectedBatchId, batches, stockItems])

  const executionReady = Boolean(
    selectedBatch?.executionNote.trim() && (selectedBatch?.photos.length ?? 0) >= 1,
  )

  const reviewTotalsBySource = useMemo(
    () => summarizeQtyBySource(filledItems),
    [filledItems],
  )

  return (
    <section className="space-y-4">
      <div className="card">
        <div>
          <p className="text-headline-md text-primary">{kitchenTerms.modules.waste}</p>
          <p className="text-body-sm text-on-surface-variant mt-1 capitalize">
            {kitchenName} · {today}
          </p>
        </div>
        <p className="text-body-sm text-status-warning mt-3 pt-3 border-t border-surface-variant bg-status-warning-bg/40 rounded-lg px-3 py-2">
          Stok baru berkurang setelah pemusnahan selesai (setelah HO menyetujui proposal).
        </p>
      </div>

      {view === 'list' && (
        <div className="space-y-3">
          {isLoading && <SkeletonList count={3} label="Memuat batch pemusnahan hari ini" />}
          <div className="card space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-body-sm text-on-surface-variant">{kitchenTerms.waste.proposal}</p>
              <button type="button" className="btn-primary" onClick={createBatch}>
                + Proposal baru
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {[
                { key: 'draft' as const, label: 'Belum dikirim', count: draftBatches.length },
                { key: 'submitted' as const, label: 'Menunggu HO', count: submittedBatches.length },
                { key: 'approved' as const, label: 'Siap musnah', count: approvedBatches.length },
                { key: 'done' as const, label: 'Selesai', count: doneBatches.length },
              ].map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  onClick={() => setListFilter(filter.key)}
                  className={`rounded-lg px-2 py-2.5 text-body-sm font-semibold leading-tight min-h-[44px] ${
                    listFilter === filter.key
                      ? 'bg-primary-container text-on-primary-container'
                      : 'bg-surface text-on-surface-variant border border-outline-variant'
                  } ${filter.key === 'approved' && filter.count > 0 && listFilter !== 'approved' ? 'ring-2 ring-status-success/40' : ''}`}
                >
                  {filter.label}
                  {filter.count > 0 && (
                    <span className="block text-xs opacity-80 mt-0.5">({filter.count})</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {visibleBatches.map((batch) => {
            const totalQty = getFilledItems(batch.items).reduce((sum, item) => sum + item.qty, 0)
            const deletable = canDeleteBatch(batch)
            const sourceSummary = formatSourceBreakdown(batch.items)
            const isRejected = batch.status === 'rejected'
            return (
              <div
                key={batch.id}
                className={`card ${isRejected ? 'border-status-danger/40 bg-error-container/30' : ''} ${batch.status === 'approved' ? 'border-status-success/30' : ''}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-label-md text-on-surface-variant">{batch.id}</p>
                    <p className="text-body-lg font-bold text-on-surface mt-1">
                      {batch.items.length > 0
                        ? `${batch.items.length} item · ${totalQty} pcs`
                        : 'Belum ada item'}
                    </p>
                    {sourceSummary && (
                      <p className="text-label-md text-on-surface-variant mt-1">{sourceSummary}</p>
                    )}
                    <p className="text-label-md text-on-surface-variant mt-2">{batch.updatedAt}</p>
                    {isRejected && batch.rejectionReason && (
                      <p className="text-body-sm text-error mt-2 bg-error-container/60 rounded-lg px-2 py-1.5 border border-error-container">
                        {batch.rejectionReason}
                      </p>
                    )}
                  </div>
                  <div className="flex items-start gap-2 shrink-0">
                    <span className={statusBadge(batch.status)}>{statusLabel(batch.status)}</span>
                    {deletable && (
                      <button
                        type="button"
                        onClick={() => requestDeleteBatch(batch.id)}
                        className="text-status-danger p-1 min-h-[44px] min-w-[44px] flex items-center justify-center"
                        aria-label="Hapus proposal kosong"
                      >
                        <span className="material-symbols-outlined text-xl">delete</span>
                      </button>
                    )}
                  </div>
                </div>
                {batch.status === 'draft' && (
                  <button
                    type="button"
                    className="btn-primary w-full mt-3 min-h-[44px]"
                    onClick={() => openProposal(batch.id)}
                  >
                    Lanjut edit
                  </button>
                )}
                {batch.status === 'approved' && (
                  <button
                    type="button"
                    className="btn-success w-full mt-3"
                    onClick={() => openExecute(batch.id)}
                  >
                    Musnah & lapor
                  </button>
                )}
                {batch.status === 'submitted' && (
                  <button
                    type="button"
                    className="btn-outline w-full mt-3 min-h-[44px]"
                    onClick={() => openDetail(batch.id)}
                  >
                    Lihat detail
                  </button>
                )}
                {batch.status === 'rejected' && (
                  <div className="flex flex-col gap-2 mt-3">
                    <button
                      type="button"
                      className="btn-primary w-full min-h-[44px]"
                      onClick={() => recreateFromRejected(batch.id)}
                    >
                      Buat ulang proposal
                    </button>
                    <button
                      type="button"
                      className="btn-outline w-full min-h-[44px]"
                      onClick={() => openDetail(batch.id)}
                    >
                      Lihat detail
                    </button>
                  </div>
                )}
                {batch.status === 'done' && (
                  <button
                    type="button"
                    className="btn-outline w-full mt-3 min-h-[44px]"
                    onClick={() => openDetail(batch.id)}
                  >
                    Lihat detail
                  </button>
                )}
              </div>
            )
          })}

          {visibleBatches.length === 0 && (
            <div className="card text-center space-y-3 text-body-sm text-on-surface-variant">
              <p>{listEmptyMessage[listFilter]}</p>
              {listFilter === 'draft' && (
                <button type="button" className="btn-primary w-full sm:w-auto" onClick={createBatch}>
                  + Proposal baru
                </button>
              )}
            </div>
          )}

          <Link href="/kitchen/home" className="btn-outline block text-center">
            Kembali ke beranda
          </Link>
        </div>
      )}

      {view === 'proposal' && selectedBatch && editorStep === 'edit' && (
        <div className="relative pb-28">
          <div className="card space-y-3">
            <div className="flex items-center justify-between gap-2 border-b border-surface-variant pb-3">
              <div>
                <p className="font-label-md text-on-surface-variant">{selectedBatch.id}</p>
                <p className="text-headline-md text-primary mt-1">{kitchenTerms.waste.proposal}</p>
                <p className="text-body-sm text-on-surface-variant">{statusLabel(selectedBatch.status)}</p>
              </div>
              <button type="button" className="btn-outline min-h-[44px]" onClick={() => openList('draft')}>
                Kembali
              </button>
            </div>

            <FlowStepper steps={[...WASTE_FLOW_STEPS]} activeStep={wasteFlowActiveStep(selectedBatch.status)} />

            {isProposalEditable && (
              <div className="rounded-lg bg-surface-container p-3 space-y-2">
                <p className="text-label-md font-semibold text-on-surface-variant">Stok tersedia hari ini</p>
                <div className="flex flex-wrap gap-2">
                  {STOCK_SOURCES.map((source) => (
                    <span
                      key={source.key}
                      className="text-body-sm text-on-surface bg-surface-card border border-outline-variant rounded-lg px-2.5 py-1.5"
                    >
                      {source.shortLabel}{' '}
                      <strong>{availableBySource[source.key]} pcs</strong>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {isProposalEditable && (
              <button
                type="button"
                className="w-full min-h-[44px] py-3 border border-dashed border-outline rounded-lg text-body-sm font-semibold text-on-surface-variant"
                onClick={openAddItem}
              >
                + Tambah item
              </button>
            )}

            {selectedBatch.items.length === 0 && isProposalEditable && (
              <div className="rounded-lg border border-dashed border-outline-variant p-4 text-center text-body-sm text-on-surface-variant">
                Belum ada item. Tambahkan produk yang akan dimusnahkan.
              </div>
            )}

            <div className="space-y-2">
              {selectedBatch.items.map((item) => {
                const limit = getItemStockLimit(
                  batches,
                  selectedBatch.id,
                  item.stockSource,
                  item.productName,
                  item.productionDate,
                  stockItems,
                )
                const over = isProposalEditable && item.qty > limit
                return (
                  <div key={item.id} className="rounded-lg border border-outline-variant p-3 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 space-y-2">
                        <p className="text-body-lg font-semibold text-on-surface">{item.productName}</p>
                        <div className="flex flex-wrap items-center gap-2">
                          <StockSourceChip source={item.stockSource} compact />
                          <span className="text-label-md text-on-surface-variant">
                            Prod. {item.productionDate}
                          </span>
                        </div>
                      </div>
                      {isProposalEditable && (
                        <button
                          type="button"
                          onClick={() => requestRemoveItem(item.id, item.productName)}
                          className="text-status-danger min-h-[44px] min-w-[44px] flex items-center justify-center"
                          aria-label="Hapus item"
                        >
                          <span className="material-symbols-outlined">delete</span>
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <QtyStepper
                        value={item.qty}
                        min={0}
                        max={isProposalEditable ? limit : undefined}
                        disabled={!isProposalEditable}
                        onChange={(qty) => updateItem(item.id, { qty })}
                      />
                      <span className="text-label-md text-on-surface-variant">
                        Stok {limit} pcs
                      </span>
                    </div>
                    {over && (
                      <p className="text-label-md text-status-danger font-medium">
                        Melebihi stok tersedia ({limit} pcs)
                      </p>
                    )}
                    {isProposalEditable && isGoodStockSource(item.stockSource) && (
                      <div className="space-y-1">
                        <label className="field-label" htmlFor={`reason_${item.id}`}>
                          Alasan pemusnahan *
                        </label>
                        <textarea
                          id={`reason_${item.id}`}
                          rows={2}
                          value={item.reason ?? ''}
                          onChange={(e) => updateItem(item.id, { reason: e.target.value })}
                          placeholder="Contoh: Kadaluarsa, rusak kemasan, sisa closing"
                          className="input-field resize-none"
                        />
                      </div>
                    )}
                    {!isProposalEditable && isGoodStockSource(item.stockSource) && (
                      <ItemReasonBlock reason={item.reason} />
                    )}
                  </div>
                )
              })}
            </div>

            <div className="space-y-2">
              <label className="field-label" htmlFor="proposal_note">
                Catatan proposal
              </label>
              <textarea
                id="proposal_note"
                rows={2}
                value={selectedBatch.proposalNote}
                onChange={(e) => updateBatch({ proposalNote: e.target.value })}
                disabled={!isProposalEditable}
                placeholder="Opsional — catatan umum proposal"
                className="input-field resize-none"
              />
            </div>

            {filledItems.length > 0 && (
              <div className="rounded-lg bg-surface-container p-3 space-y-2">
                <div className="flex justify-between">
                  <span className="text-body-sm text-on-surface-variant">Total</span>
                  <span className="text-body-lg font-bold">
                    {filledItems.length} item · {filledTotalQty} pcs
                  </span>
                </div>
                <p className="text-label-md text-on-surface-variant">{formatSourceBreakdown(filledItems)}</p>
              </div>
            )}

            {saveError && (
              <div role="alert" className="alert-error">
                <span className="material-symbols-outlined text-base shrink-0">error</span>
                <span>{saveError}</span>
              </div>
            )}
          </div>

          {isProposalEditable && (
            <StickyActionFooter>
              <button type="button" className="btn-outline w-full min-h-[44px]" onClick={saveDraft}>
                Simpan draft
              </button>
              <button type="button" className="btn-primary w-full min-h-[44px]" onClick={openSubmitReview}>
                Kirim ke HO
              </button>
              <p className="text-label-md text-on-surface-variant text-center">
                Stok belum berubah sampai pemusnahan selesai.
              </p>
            </StickyActionFooter>
          )}
        </div>
      )}

      {view === 'proposal' && selectedBatch && editorStep === 'review' && (
        <div className="card space-y-3">
          <div className="flex items-center justify-between gap-2 border-b border-surface-variant pb-3">
            <div>
              <p className="text-headline-md text-primary">Konfirmasi kirim ke HO</p>
              <p className="text-body-sm text-on-surface-variant">Periksa proposal sebelum dikirim</p>
            </div>
            <button type="button" className="btn-outline min-h-[44px]" onClick={() => setEditorStep('edit')}>
              Kembali
            </button>
          </div>

          <FlowStepper steps={[...WASTE_FLOW_STEPS]} activeStep={wasteFlowActiveStep('draft')} />

          <div className="flex flex-wrap gap-2">
            {STOCK_SOURCES.filter((s) => reviewTotalsBySource[s.key] > 0).map((source) => (
              <span
                key={source.key}
                className="text-body-sm bg-surface-container border border-outline-variant rounded-lg px-3 py-1.5"
              >
                {source.shortLabel}: <strong>{reviewTotalsBySource[source.key]} pcs</strong>
              </span>
            ))}
          </div>

          <div className="rounded-lg border border-outline-variant divide-y divide-surface-variant">
            {filledItems.map((item) => (
              <div key={item.id} className="flex justify-between gap-3 p-3">
                <div className="space-y-1.5">
                  <p className="text-body-lg font-semibold">{item.productName}</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <StockSourceChip source={item.stockSource} compact />
                    <span className="text-label-md text-on-surface-variant">{item.productionDate}</span>
                  </div>
                  {isGoodStockSource(item.stockSource) && <ItemReasonBlock reason={item.reason} />}
                </div>
                <p className="text-body-lg font-bold text-primary shrink-0">{item.qty} pcs</p>
              </div>
            ))}
          </div>

          {selectedBatch.proposalNote.trim() && (
            <div className="rounded-lg border border-outline-variant p-3">
              <p className="text-label-md text-on-surface-variant">Catatan</p>
              <p className="text-body-sm mt-1">{selectedBatch.proposalNote}</p>
            </div>
          )}

          <p className="text-body-sm text-status-warning bg-status-warning-bg/40 rounded-lg px-3 py-2">
            Setelah dikirim, stok belum berubah sampai HO menyetujui dan pemusnahan selesai.
          </p>

          <button
            type="button"
            className="btn-primary w-full min-h-[44px]"
            onClick={() => setPinMode('submit')}
          >
            Konfirmasi dengan PIN
          </button>
        </div>
      )}

      {view === 'execute' && selectedBatch && selectedBatch.status === 'approved' && (
        <div className="card space-y-3">
          <div className="flex items-center justify-between gap-2 border-b border-surface-variant pb-3">
            <div>
              <p className="text-headline-md text-primary">Musnah · {selectedBatch.id}</p>
              <p className="text-body-sm text-on-surface-variant">Item disetujui HO</p>
            </div>
            <span className={statusBadge(selectedBatch.status)}>{statusLabel(selectedBatch.status)}</span>
          </div>

          <button type="button" className="btn-outline w-full min-h-[44px]" onClick={() => openList('approved')}>
            Kembali ke daftar
          </button>

          <FlowStepper steps={[...WASTE_FLOW_STEPS]} activeStep={wasteFlowActiveStep(selectedBatch.status)} />

          <p className="text-body-sm text-status-warning bg-status-warning-bg/40 rounded-lg px-3 py-2">
            Setelah dikonfirmasi, status selesai dan stok langsung berkurang.
          </p>

          <div className="space-y-2">
            {selectedBatch.items.map((item) => (
              <div key={item.id} className="rounded-lg bg-surface-container p-3 space-y-2">
                <p className="text-body-lg font-semibold">{item.productName}</p>
                <div className="flex flex-wrap items-center gap-2">
                  <StockSourceChip source={item.stockSource} compact />
                  <span className="text-body-sm text-on-surface-variant">
                    {item.qty} pcs · {item.productionDate}
                  </span>
                </div>
                {isGoodStockSource(item.stockSource) && <ItemReasonBlock reason={item.reason} />}
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-outline-variant p-3 space-y-2">
            <p className="text-label-md font-semibold text-on-surface-variant">Checklist sebelum selesai</p>
            <div className="flex items-center gap-2 text-body-sm">
              <span
                className={`material-symbols-outlined ${selectedBatch.executionNote.trim() ? 'text-status-success' : 'text-on-surface-variant'}`}
              >
                {selectedBatch.executionNote.trim() ? 'check_circle' : 'radio_button_unchecked'}
              </span>
              Catatan pemusnahan diisi
            </div>
            <div className="flex items-center gap-2 text-body-sm">
              <span
                className={`material-symbols-outlined ${selectedBatch.photos.length >= 1 ? 'text-status-success' : 'text-on-surface-variant'}`}
              >
                {selectedBatch.photos.length >= 1 ? 'check_circle' : 'radio_button_unchecked'}
              </span>
              Min. 1 foto bukti ({selectedBatch.photos.length} terlampir)
            </div>
          </div>

          <div className="space-y-2">
            <label className="field-label" htmlFor="execution_note">
              Catatan pemusnahan *
            </label>
            <textarea
              id="execution_note"
              rows={2}
              value={selectedBatch.executionNote}
              onChange={(e) => updateBatch({ executionNote: e.target.value })}
              placeholder="Contoh: Sudah dibuang ke tempat sampah dapur"
              className="input-field resize-none"
            />
          </div>

          <div className="space-y-2">
            <p className="field-label">Foto bukti * (min 1)</p>
            <label className="btn-outline w-full min-h-[44px] inline-flex items-center justify-center gap-2 cursor-pointer">
              <span className="material-symbols-outlined">photo_camera</span>
              {selectedBatch.photos.length > 0 ? 'Tambah foto lagi' : 'Ambil / pilih foto'}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                className="sr-only"
                onChange={(e) => handlePhotosSelected(e.target.files)}
              />
            </label>
            {selectedBatch.photos.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedBatch.photos.map((src, index) => (
                  <div
                    key={`${src}-${index}`}
                    className="relative w-20 h-20 rounded-lg overflow-hidden border border-outline-variant bg-surface-container flex items-center justify-center"
                  >
                    {src.startsWith('blob:') || src.startsWith('http') ? (
                      <img src={src} alt={`Bukti ${index + 1}`} className="w-full h-full object-cover" />
                    ) : (
                      <span className="material-symbols-outlined text-3xl text-on-surface-variant">
                        image
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute top-0 right-0 bg-inverse-surface/70 text-white p-0.5"
                      aria-label="Hapus foto"
                    >
                      <span className="material-symbols-outlined text-sm">close</span>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {saveError && (
            <div role="alert" className="alert-error">
              <span className="material-symbols-outlined text-base shrink-0">error</span>
              <span>{saveError}</span>
            </div>
          )}

          <button
            type="button"
            className="btn-primary w-full min-h-[44px] disabled:opacity-50"
            disabled={!executionReady}
            onClick={openExecutePin}
          >
            Selesai & kurangi stok
          </button>
        </div>
      )}

      {view === 'detail' && selectedBatch && (
        <div className="card space-y-3">
          <div className="flex items-center justify-between gap-2 border-b border-surface-variant pb-3">
            <div>
              <p className="font-label-md text-on-surface-variant">{selectedBatch.id}</p>
              <p className="text-headline-md text-primary mt-1">Detail pemusnahan</p>
              <span className={`${statusBadge(selectedBatch.status)} mt-2 inline-block`}>
                {statusLabel(selectedBatch.status)}
              </span>
            </div>
            <button
              type="button"
              className="btn-outline min-h-[44px]"
              onClick={() =>
                openList(
                  selectedBatch.status === 'draft'
                    ? 'draft'
                    : selectedBatch.status === 'approved'
                      ? 'approved'
                      : selectedBatch.status === 'done'
                        ? 'done'
                        : 'submitted',
                )
              }
            >
              Kembali
            </button>
          </div>

          <FlowStepper steps={[...WASTE_FLOW_STEPS]} activeStep={wasteFlowActiveStep(selectedBatch.status)} />

          {selectedBatch.status === 'rejected' && selectedBatch.rejectionReason && (
            <div className="rounded-lg border border-status-danger/30 bg-error-container/60 p-3 text-body-sm text-error">
              <p className="font-semibold">Alasan penolakan HO</p>
              <p className="mt-1">{selectedBatch.rejectionReason}</p>
            </div>
          )}

          <div className="space-y-2">
            {selectedBatch.items.map((item) => (
              <div key={item.id} className="rounded-lg border border-outline-variant p-3 space-y-2">
                <p className="text-body-lg font-semibold">{item.productName}</p>
                <div className="flex flex-wrap items-center gap-2">
                  <StockSourceChip source={item.stockSource} compact />
                  <span className="text-body-sm text-on-surface-variant">
                    {item.qty} pcs · {item.productionDate}
                  </span>
                </div>
                {isGoodStockSource(item.stockSource) && <ItemReasonBlock reason={item.reason} />}
              </div>
            ))}
          </div>

          {selectedBatch.proposalNote && (
            <div className="rounded-lg bg-surface-container p-3 text-body-sm">
              <p className="text-label-md text-on-surface-variant">Catatan proposal</p>
              <p className="mt-1">{selectedBatch.proposalNote}</p>
            </div>
          )}

          {selectedBatch.executionNote && (
            <div className="rounded-lg bg-surface-container p-3 text-body-sm">
              <p className="text-label-md text-on-surface-variant">Catatan pemusnahan</p>
              <p className="mt-1">{selectedBatch.executionNote}</p>
            </div>
          )}

          {selectedBatch.photos.length > 0 && (
            <div className="space-y-2">
              <p className="text-label-md font-semibold text-on-surface-variant">Foto bukti</p>
              <div className="flex flex-wrap gap-2">
                {selectedBatch.photos.map((src, index) => (
                  <div
                    key={`${src}-${index}`}
                    className="w-20 h-20 rounded-lg overflow-hidden border border-outline-variant bg-surface-container flex items-center justify-center"
                  >
                    {src.startsWith('blob:') || src.startsWith('http') ? (
                      <img src={src} alt={`Bukti ${index + 1}`} className="w-full h-full object-cover" />
                    ) : (
                      <span className="material-symbols-outlined text-3xl text-on-surface-variant">
                        image
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <WasteTimeline batch={selectedBatch} />

          {selectedBatch.status === 'rejected' && (
            <button
              type="button"
              className="btn-primary w-full min-h-[44px]"
              onClick={() => recreateFromRejected(selectedBatch.id)}
            >
              Buat ulang proposal
            </button>
          )}
        </div>
      )}

      {isAddItemOpen && (
        <div className="fixed inset-0 z-30 bg-inverse-surface/40 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="w-full max-w-md max-h-[90vh] flex flex-col bg-surface-card border border-outline-variant rounded-t-xl sm:rounded-xl shadow-sm">
            <div className="px-4 py-3 border-b border-surface-variant flex justify-between items-center shrink-0">
              <p className="text-headline-md text-primary">Tambah item</p>
              <button
                type="button"
                onClick={() => setIsAddItemOpen(false)}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Tutup"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto flex-1">
              <div className="space-y-2">
                <p className="field-label">Sumber stok</p>
                <div className="grid grid-cols-2 gap-2">
                  {STOCK_SOURCES.map((source) => (
                    <button
                      key={source.key}
                      type="button"
                      onClick={() => selectSourceInModal(source.key)}
                      className={`rounded-lg px-2 py-2.5 text-body-sm font-semibold leading-tight min-h-[44px] ${
                        newItemSource === source.key
                          ? 'bg-primary-container text-on-primary-container'
                          : 'bg-surface text-on-surface-variant border border-outline-variant'
                      }`}
                    >
                      {source.shortLabel}
                    </button>
                  ))}
                </div>
                <p className="text-label-md text-on-surface-variant">
                  {stockSourceMeta(newItemSource).hint}
                </p>
              </div>

              <div className="space-y-2">
                <p className="field-label">Pilih produk</p>
                {stockOptionsForSource.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-outline-variant p-4 text-center text-body-sm text-on-surface-variant">
                    Tidak ada stok {stockSourceMeta(newItemSource).shortLabel.toLowerCase()} tersedia
                    untuk ditambahkan.
                  </div>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {stockOptionsForSource.map((item) => {
                      const key = stockBucketKey(item.source, item.productName, item.productionDate)
                      const available = remainingStock.get(key) ?? 0
                      const selected = newItemProduct === key
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => {
                            setNewItemProduct(key)
                            setNewItemQty(Math.min(newItemQty || 1, available))
                            setAddItemError(null)
                          }}
                          className={`w-full text-left rounded-lg border p-3 min-h-[44px] transition-colors ${
                            selected
                              ? 'border-primary bg-primary-container/20'
                              : 'border-outline-variant bg-surface-container-low hover:border-primary/50'
                          }`}
                        >
                          <p className="text-body-lg font-semibold text-on-surface">{item.productName}</p>
                          <p className="text-label-md text-on-surface-variant mt-1">
                            Prod. {item.productionDate} · stok {available} pcs
                          </p>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>

              {newItemProduct && newItemLimit > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="field-label">Jumlah</p>
                    <button
                      type="button"
                      className="text-body-sm font-semibold text-primary"
                      onClick={() => setNewItemQty(newItemLimit)}
                    >
                      Isi maks ({newItemLimit})
                    </button>
                  </div>
                  <QtyStepper
                    value={newItemQty}
                    min={1}
                    max={newItemLimit}
                    onChange={setNewItemQty}
                  />
                </div>
              )}

              {addItemError && (
                <div role="alert" className="alert-error">
                  <span className="material-symbols-outlined text-base shrink-0">error</span>
                  <span>{addItemError}</span>
                </div>
              )}
            </div>
            <div className="px-4 py-3 border-t border-surface-variant flex gap-2 shrink-0 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              <button
                type="button"
                className="btn-outline flex-1 min-h-[44px]"
                onClick={() => setIsAddItemOpen(false)}
              >
                Batal
              </button>
              <button
                type="button"
                className="btn-primary flex-1 min-h-[44px] disabled:opacity-50"
                disabled={!newItemProduct || newItemLimit <= 0}
                onClick={confirmAddItem}
              >
                Tambah
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmAction && (
        <ConfirmDialog
          title={confirmAction.type === 'deleteItem' ? 'Hapus item?' : 'Hapus proposal kosong?'}
          message={
            confirmAction.type === 'deleteItem'
              ? `Hapus "${confirmAction.label}" dari proposal ini?`
              : 'Proposal kosong akan dihapus permanen dari daftar draft.'
          }
          confirmLabel="Hapus"
          onConfirm={handleConfirmAction}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      <PinKeypadModal
        open={pinMode === 'submit'}
        title="PIN konfirmasi"
        description={`Masukkan PIN untuk ${kitchenTerms.waste.submitHo.toLowerCase()}.`}
        confirmLabel={kitchenTerms.waste.submitHo}
        onClose={() => setPinMode(null)}
        onConfirm={confirmSubmitToHo}
      />

      <PinKeypadModal
        open={pinMode === 'execute'}
        title="PIN konfirmasi"
        description="Masukkan PIN untuk menyelesaikan pemusnahan dan mengurangi stok."
        confirmLabel="Selesai pemusnahan"
        onClose={() => setPinMode(null)}
        onConfirm={confirmExecute}
      />

      {toast && <Toast message={toast} onDismiss={dismissToast} />}
    </section>
  )
}
